import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Card } from './card'

type Tone = 'neutral' | 'warning' | 'critical'

const TONES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  warning: 'bg-warning/15 text-warning',
  critical: 'bg-critical/10 text-critical',
}

/** The "nothing here" state: an empty list, a search with no hits, a screen
 *  whose data does not exist yet.
 *
 * One component rather than a bare paragraph per page, because an empty state
 * is where a user is most likely to be stuck — it should say what happened and
 * what to do next, in the same shape every time.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  tone = 'neutral',
  action,
  children,
  className,
  bare = false,
}: {
  icon?: LucideIcon
  title: string
  description?: string
  tone?: Tone
  action?: ReactNode
  /** Extra detail below the description — a list of what is missing, say. */
  children?: ReactNode
  className?: string
  /** Render without the surrounding Card, for use inside one. */
  bare?: boolean
}) {
  const body = (
    <div className={cn('px-6 py-10 text-center', bare && 'px-0 py-8', className)}>
      {Icon && (
        <span className={cn('mx-auto grid size-10 place-items-center rounded-lg', TONES[tone])}>
          <Icon className="size-5" aria-hidden />
        </span>
      )}
      <h2 className={cn('text-sm font-semibold', Icon && 'mt-3')}>{title}</h2>
      {description && (
        <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">{description}</p>
      )}
      {children}
      {action && <div className="mt-4 flex justify-center gap-2">{action}</div>}
    </div>
  )

  return bare ? body : <Card className="mx-auto max-w-xl">{body}</Card>
}
